import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ImpactoAtendido {
  id: number;
  descripcion: string;
}

export interface MedidaManejoReporte {
  id: number;
  medida_manejo: string;
  tipo_medida: string;
  impactos_atendidos_ids: number[];
  evidencias: any[];
  ejecucion_periodo: string;
  parametro_control_descripcion?: string;
  parametro_control_valor?: string;
  valor_referencia_descripcion?: string;
  valor_referencia_valor?: string;
  cumplimiento_si_no?: string;
  indicadores?: IndicadorReporte[];
}

export interface IndicadorReporte {
  meta_descripcion: string;
  periodicidad_verificacion?: string;
  porcentaje_cumplimiento?: string;
  porcentaje_avance_programado?: string;
  porcentaje_avance_fecha?: string;
  observaciones?: string;
}

export interface ObservacionReporte {
  id: number;
  descripcion: string;
}

export interface DatosReporte {
  obligacion: {
    id: string;
    codigo: string;
    nombre_programa: string;
    tipo: string;
    objetivo: string;
    nivel_cumplimiento: number;
  };
  planManejo: {
    id: string;
    numero_ficha: string;
    titulo_programa: string;
    created_at: string;
    impactos_atendidos: ImpactoAtendido[];
    medidas_manejo: MedidaManejoReporte[];
    indicadores: IndicadorReporte[];
    observaciones: ObservacionReporte[];
    items_metas?: any[];
    items_acciones?: any[];
    observaciones_generales?: string;
  } | null;
}

export const useReporteAmbiental = (obligacionId?: string) => {
  const [datosReporte, setDatosReporte] = useState<DatosReporte | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const cargarDatosReporte = async (id: string) => {
    try {
      setLoading(true);

      // Cargar obligación
      const { data: obligacion, error: errorObligacion } = await supabase
        .from('obligaciones_ambientales')
        .select('*')
        .eq('id', id)
        .single();

      if (errorObligacion) throw errorObligacion;

      // Cargar plan de manejo más reciente (sin filtrar por user_id, las RLS se encargan)
      const { data: planesManejo, error: errorPlan } = await supabase
        .from('planes_manejo')
        .select('*')
        .eq('obligacion_id', id)
        .order('created_at', { ascending: false });

      if (errorPlan) throw errorPlan;

      // Tomar el primer plan (el más reciente)
      const planManejo = planesManejo && planesManejo.length > 0 ? planesManejo[0] : null;

      setDatosReporte({
        obligacion: {
          id: obligacion.id,
          codigo: obligacion.codigo,
          nombre_programa: obligacion.nombre_programa,
          tipo: obligacion.tipo,
          objetivo: obligacion.objetivo || '',
          nivel_cumplimiento: obligacion.nivel_cumplimiento || 0,
        },
        planManejo: planManejo ? {
          id: planManejo.id,
          numero_ficha: planManejo.numero_ficha || '',
          titulo_programa: planManejo.titulo_programa || '',
          created_at: planManejo.created_at,
          impactos_atendidos: Array.isArray(planManejo.impactos_atendidos) ? planManejo.impactos_atendidos as unknown as ImpactoAtendido[] : [],
          medidas_manejo: Array.isArray(planManejo.medidas_manejo) ? planManejo.medidas_manejo as unknown as MedidaManejoReporte[] : [],
          indicadores: Array.isArray(planManejo.indicadores) ? planManejo.indicadores as unknown as IndicadorReporte[] : [],
          observaciones: Array.isArray(planManejo.observaciones) ? planManejo.observaciones as unknown as ObservacionReporte[] : [],
          items_metas: Array.isArray(planManejo.items_metas) ? planManejo.items_metas as any[] : [],
          items_acciones: Array.isArray(planManejo.items_acciones) ? planManejo.items_acciones as any[] : [],
          observaciones_generales: planManejo.observaciones_generales || '',
        } : null,
      });

    } catch (error: any) {
      console.error('Error al cargar datos del reporte:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos del reporte',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (obligacionId) {
      cargarDatosReporte(obligacionId);
    }
  }, [obligacionId]);

  return {
    datosReporte,
    loading,
    cargarDatosReporte,
  };
};
